// Staff softphone web push (roadmap #41). Ported from qr-phone-frontend
// firebase.service.tsx (messaging half) + App.jsx (foreground onMessage + the
// service-worker message bridge). The FCM contract is unchanged: the token is
// registered with POST /users/fcmtoken/update and silent notifications carry the
// same action names (FCM-INCOMING-CALL, FCM-KILL-NOTIFICATION-CALL,
// FCM-LOGOUT-SESSION) and the same payload fields the backend already sends.
import { getMessaging, getToken, onMessage } from 'firebase/messaging'
import { app } from '../core/firebase'
import { globals } from '../core/globals'
import { httpRequest } from '../core/http'
import { updateCallStatusByUserId } from '../core/callStatus'
import { setStoredFcmToken, staffLogout } from './staffAuth'

// SAME WEB PUSH KEY AS THE PREVIOUS CLIENT (SAME FIREBASE PROJECT).
const VAPID_KEY = 'BF4X6ryYiTfK4dqPsQcfAiceSjmNXynFQPSoPcvEX-TGgaTyR8Ti6mxHFTIZaDvCBL1n2uGPr54fcObjPEWrKpw'

let messaging: ReturnType<typeof getMessaging> | null = null
const getMessagingInstance = () => {
  if (!messaging) messaging = getMessaging(app)
  return messaging
}

// SEND THE DEVICE FCM TOKEN TO THE SERVER SO THIS WEB DEVICE RECEIVES CALL NOTIFICATIONS.
export const updateFCMToken = async (userId: number, token: string) => {
  return httpRequest('users/fcmtoken/update', 'POST', { token, user_id: userId, platform: 'web' })
}

const fetchFcmToken = async (userId: number) => {
  try {
    const token = await getToken(getMessagingInstance(), { vapidKey: VAPID_KEY })
    if (token) {
      setStoredFcmToken(token)
      await updateFCMToken(userId, token)
    }
  } catch (err) {
    console.log('Error getting FCM token:', err)
  }
}

// REQUEST NOTIFICATION PERMISSION (IF NEEDED) AND REGISTER THE TOKEN. WITHOUT THIS THE
// STAFF DEVICE NEVER RECEIVES INCOMING-CALL PUSHES.
export const registerFcmToken = (userId: number) => {
  if (typeof Notification === 'undefined') return
  if (Notification.permission !== 'granted') {
    Notification.requestPermission().then((permission) => {
      if (permission === 'granted') fetchFcmToken(userId)
      else console.log('Notification permission denied', permission)
    })
  } else {
    fetchFcmToken(userId)
  }
}

// REGISTER THE BACKGROUND MESSAGING SERVICE WORKER (SHOWS THE OS NOTIFICATION AND BRIDGES
// THE ACTION BACK TO THE APP WHILE NO TAB IS FOCUSED).
export const registerMessagingServiceWorker = () => {
  if (!('serviceWorker' in navigator)) return
  navigator.serviceWorker
    .register('/firebase-messaging-sw.js')
    .then((registration) => console.log('Messaging service worker registered:', registration.scope))
    .catch((err) => console.error('Service worker registration failed:', err))
}

// HANDLE A SILENT/BACKGROUND NOTIFICATION (FOREGROUND PATH). MIRRORS
// processSilentNotification in the previous client: it primes the shared call state and
// routes the staff to the incoming-call screen.
export const processSilentNotification = async (data: any, navigate: (path: string, opts?: any) => void) => {
  const action: string = data?.action

  if (action === 'FCM-LOGOUT-SESSION') {
    staffLogout()
    navigate('/staff')
    return
  }

  if (action === 'FCM-KILL-NOTIFICATION-CALL' && globals.user_id === data.user_id) {
    globals.callerSocketID = null
    globals.callerUserName = null
    globals.callerDetail = null
    globals.call_id = null
    globals.callStaffData = null
    globals.notification_received_time = null
    globals.userFromNotification = false
    navigate('/staff/dashboard')
    return
  }

  if (action === 'FCM-INCOMING-CALL') {
    globals.callerSocketID = data.body
    globals.callerUserName = data.name
    globals.callerDetail = data.callerDetail ? JSON.parse(data.callerDetail) : null
    globals.call_id = data.call_id
    globals.maxResponseSeconds = data.maxResponseSeconds
    globals.enabledDirectCall = data?.enabledDirectCall
    globals.isThisDefaultStaffNotified = data.isThisDefaultStaffNotified
    globals.callStaffData = data.staffs
    globals.notification_received_time = data.time
    globals.userFromNotification = false
    await updateCallStatusByUserId(data.call_id, 'FCMPR', data.user_id)

    if (globals.currentScreenName !== 'calling') {
      navigate('/staff/incomingcall', { state: { callArrivedAt: Date.now() } })
    }
  }
}

// FOREGROUND MESSAGES (TAB OPEN): ROUTE THEM THROUGH THE SAME SILENT-NOTIFICATION HANDLER.
export const subscribeForegroundMessages = (navigate: (path: string, opts?: any) => void) => {
  onMessage(getMessagingInstance(), (notification) => {
    const data = notification?.data
    if (data && (typeof data.type !== 'undefined' || typeof data.action !== 'undefined')) {
      processSilentNotification(data, navigate)
    }
  })
}

// PRIME THE SHARED CALL STATE FROM AN INCOMING-CALL NOTIFICATION PAYLOAD AND MARK THE CALL AS
// RECEIVED (FCMPR). SHARED BY THE PLAIN BRIDGE (open the ringing screen) AND THE ACCEPT/REJECT
// NOTIFICATION ACTIONS (#55).
const primeIncomingCallFromPayload = async (payload: any) => {
  globals.callerSocketID = payload.body
  globals.callerUserName = payload.name
  globals.callerDetail = payload.callerDetail ? JSON.parse(payload.callerDetail) : null
  globals.call_id = payload.call_id
  globals.enabledDirectCall = payload?.enabledDirectCall
  globals.isThisDefaultStaffNotified = payload.isThisDefaultStaffNotified
  globals.callStaffData = payload.staffs
  globals.maxResponseSeconds = payload.maxResponseSeconds || globals.maxResponseSeconds
  globals.notification_received_time = payload.time
  globals.userFromNotification = false
  await updateCallStatusByUserId(payload.call_id, 'FCMPR', payload.user_id)
}

// BRIDGE MESSAGES POSTED BY THE SERVICE WORKER (BACKGROUND NOTIFICATION INTERACTIONS).
// INCOMING_CALL_ACTION opens the in-app incoming-call screen; INCOMING_CALL_ACCEPT /
// INCOMING_CALL_REJECT (#55) come from the notification's Accept/Decline buttons and drive the
// in-app answer/reject via a navigation flag the ringing screen acts on; STOP_RINGTONE asks the
// screen to stop (another staff answered / the notification was dismissed).
export const subscribeServiceWorkerMessages = (
  navigate: (path: string, opts?: any) => void,
  onStopRingtone: () => void
) => {
  if (!('serviceWorker' in navigator)) return () => {}
  const handler = async (event: MessageEvent) => {
    const data = event.data
    if (!data) return
    const { type, payload, call_id } = data

    if (type === 'STOP_RINGTONE') {
      onStopRingtone()
      return
    }

    if (
      (type === 'INCOMING_CALL_ACTION' ||
        type === 'INCOMING_CALL_ACCEPT' ||
        type === 'INCOMING_CALL_REJECT') &&
      payload &&
      typeof payload === 'object'
    ) {
      await primeIncomingCallFromPayload(payload)
      // The ringing screen reads `autoAccept`/`autoReject` from the navigation state and performs
      // the same accept/reject the in-app buttons do (no protocol duplicated here).
      const autoAccept = type === 'INCOMING_CALL_ACCEPT'
      const autoReject = type === 'INCOMING_CALL_REJECT'
      if (globals.currentScreenName !== 'calling' || autoAccept || autoReject) {
        navigate('/staff/incomingcall', { state: { callArrivedAt: Date.now(), autoAccept, autoReject } })
      }
    } else if (type === 'STOP_RINGTONE_FOR' && call_id && call_id === globals.call_id) {
      onStopRingtone()
    }
  }
  navigator.serviceWorker.addEventListener('message', handler)
  return () => navigator.serviceWorker.removeEventListener('message', handler)
}
